// Angular imports
import {
    Component,
    OnInit,
    Input,
    ViewChild,
    Output,
    EventEmitter,
    SimpleChanges,
    OnChanges,
    OnDestroy
} from '@angular/core';
import { Router } from '@angular/router';

// devextreme imports
import { DxTreeViewComponent } from 'devextreme-angular';

// Component imports
import { CurrentOwnerQuickSearchComponent } from '../current-owner-quick-search/current-owner-quick-search.component';
import { CommunityQuickSearchComponent } from '../community-quick-search/community-quick-search.component';

// service imports
import { BookmarkService } from '../../_services/shared-services/bookmark.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { NavigationService } from '../../_services/shared-services/navigation.service';
import { CommunityService } from '../../_services/data-services/community.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';

// CC-Common imports
import { Utils } from 'cc-common';

// 3rd party imports
import { Subscription } from 'rxjs';

// model imports
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
import { PopMenuInfo } from '../../_models/pop-menu-info';
import { BookmarkInfo } from '../../_models/bookmark-info';
import { SideNavigationTree } from '../../_constants/side-navigation-tree';
import { OwnerSearchResultDto } from '../../search/_models/owner-search-result-dto';
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';

interface SearchButtonsItem {
    name: string;
    icon: string;
    hint: string;
    selected: boolean;
    visible: boolean;
}

@Component({
    selector: 'app-side-navigation-search-toolbar',
    templateUrl: './side-navigation-search-toolbar.component.html',
    styleUrls: ['./side-navigation-search-toolbar.component.scss']
})
export class SideNavigationSearchToolbarComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('ownerSearch', { static: false }) ownerSearch: CurrentOwnerQuickSearchComponent;
    @ViewChild('communitySearch', { static: false }) communitySearch: CommunityQuickSearchComponent;
    @ViewChild('bookmarkTree', { static: false }) bookmarkTree: DxTreeViewComponent;

    @Input() visible: boolean;
    @Input() menuItems: SideNavTreeInfo[];
    @Input() selectedNode: SideNavTreeInfo;
    @Output() onNavigationChange = new EventEmitter<string>();

    searchButtons: SearchButtonsItem[];
    loadingVisible = false;
    selectedButton: any;
    isSearchPanelAvailable: boolean;

    ownerSearchedText: string;
    lastOwnerSearchText: string;
    ownerSearchResult: OwnerSearchResultDto[] = [];

    communitySearchedText: string;
    lastCommunitySearchText: string;
    communitySearchResult: CommunitySelectorResultDto[] = [];
    communities: CommunitySelectorResultDto[] = [];

    showMenu = false;
    isParent = false;
    sideMenuItems: PopMenuInfo[];
    bookmarkMenuItems: PopMenuInfo[];

    showBookmarkPopover = false;
    showAddBookmarkPopup = false;

    savedBookmarkItems: BookmarkInfo[] = [];
    masterNavItems: SideNavTreeInfo[] = [];
    bookmarkItems: PopMenuInfo[] = [];
    bookmarkIcon: string;

    contextSubscription: Subscription;
    selectedContextCommunityId: number;

    constructor(
        private readonly bookmarkService: BookmarkService,
        private readonly helperService: HelperService,
        private readonly navigationService: NavigationService,
        private readonly errorService: ServerErrorService,
        private readonly communityService: CommunityService,
        private readonly contextManagerService: ContextManagerService,
        private readonly router: Router
    ) {}

    ngOnInit() {
        this.populateCommunities();
        this.masterNavItems = SideNavigationTree.masterTreeItems;

        this.savedBookmarkItems = this.bookmarkService.getSavedBookmarkItems();
        this.prepareBookmarkItems();
        this.bookmarkIcon =
            this.savedBookmarkItems && this.savedBookmarkItems.length > 0 ? 'fas fa-bookmark' : 'far fa-bookmark';

        this.updateSelectedContext();
        this.contextSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });

        this.bookmarkService.getBookmarkSaveNotify().subscribe((items: BookmarkInfo[]) => {
            this.savedBookmarkItems = items;
            this.bookmarkItems = [];
            this.prepareBookmarkItems();

            this.bookmarkIcon =
                this.savedBookmarkItems && this.savedBookmarkItems.length > 0 ? 'fas fa-bookmark' : 'far fa-bookmark';
            this.showAddBookmarkPopup = false;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.menuItems && changes.menuItems.currentValue) {
            this.menuItems = changes.menuItems.currentValue;
            this.sideMenuItems = this.helperService.prepareMenuItems(this.menuItems);
            this.prepareBookmarkItems();
        }

        if (changes.selectedNode && changes.selectedNode.currentValue) {
            this.selectedNode = changes.selectedNode.currentValue;
            if (Utils.equalsIgnoreCase(this.selectedNode.id, 't1')) {
                this.populateSearchButtons();
            }
        }
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.contextSubscription)) {
            this.contextSubscription.unsubscribe();
        }
    }

    onSearchItemClick(selectedButton): void {
        this.searchButtons.forEach((button) => {
            button.selected = false;
            if (selectedButton.name === button.name) {
                button.selected = true;
                selectedButton.selected = true;
            }
        });
        this.selectedButton = selectedButton;

        if (this.selectedButton.name === 'owner') {
            if (this.ownerSearchedText) {
                this.isSearchPanelAvailable = true;
            } else {
                this.isSearchPanelAvailable = false;
            }
        } else if (this.selectedButton.name === 'community') {
            if (this.communitySearchedText) {
                this.isSearchPanelAvailable = true;
            } else {
                this.isSearchPanelAvailable = false;
            }
        } else if (this.selectedButton.name === 'singleCommunity') {
            const url = `community/${this.communities[0].communityId}/home`;
            this.router.navigate([url]);
        } else {
            this.isSearchPanelAvailable = false;
        }
    }

    onOwnerTextSearched(event): void {
        if (event) {
            this.ownerSearchedText = event;
            this.isSearchPanelAvailable = true;
        } else {
            this.isSearchPanelAvailable = false;
        }
    }

    saveOwnerSearchResults(event): void {
        if (event) {
            this.lastOwnerSearchText = this.ownerSearchedText;
            this.ownerSearchResult = event;
        }
    }

    onCommunityTextSearched(event): void {
        if (event) {
            this.communitySearchedText = event;
            this.isSearchPanelAvailable = true;
        } else {
            this.isSearchPanelAvailable = false;
        }
    }

    saveCommunitySearchResults(event): void {
        if (event) {
            this.lastCommunitySearchText = this.communitySearchedText;
            this.communitySearchResult = event;
        }
    }

    openLastSearchedPanel(): void {
        if (this.selectedButton.name === 'owner') {
            this.ownerSearch.showLastSearchedResult(this.ownerSearchedText);
        } else if (this.selectedButton.name === 'community') {
            this.communitySearch.showLastSearchedResult(this.communitySearchedText);
        }
    }

    openSideMenu(): void {
        this.showMenu = true;
    }

    onNavigationChanged(e: any): void {
        const itemData = e.itemData;
        if (itemData.path) {
            this.onNavigationChange.emit(itemData.path);
        } else if (itemData.isWorkspace) {
            this.navigationService.updateWorkspaceSelection(itemData);
        }
        this.showMenu = false;
    }

    onPopoverShowing(event): void {
        const el = event.component.content().parentElement;
        el.id = 'popover-content';
    }

    openBookmarkPopover(): void {
        this.showBookmarkPopover = true;
    }

    addBookmarks(): void {
        this.savedBookmarkItems = this.bookmarkService.getSavedBookmarkItems();
        this.prepareBookmarkItems();
        this.showAddBookmarkPopup = true;
    }

    onBookmarkClose(event): void {
        this.showAddBookmarkPopup = false;
    }

    onBookmarkItemSelection(event): void {
        const itemData = event.itemData;
        if (!itemData.disable) {
            this.onNavigationChange.emit(itemData.path);
            this.showBookmarkPopover = false;
        }
    }

    private populateSearchButtons(): void {
        this.searchButtons = [];
        this.searchButtons.push({
            name: 'owner',
            icon: 'fas fa-house-user',
            hint: 'Owner Search',
            selected: true,
            visible: true
        });

        if (Utils.equals(this.communities.length, 1)) {
            this.searchButtons.push({
                name: 'singleCommunity',
                icon: 'fas fa-location-arrow',
                hint: 'Navigate To Community',
                selected: false,
                visible: Utils.isNullOrUndefined(this.selectedContextCommunityId)
            });
        } else {
            this.searchButtons.push({
                name: 'community',
                icon: 'fas fa-map-marker-alt',
                hint: 'Community Search',
                selected: false,
                visible: true
            });
        }

        this.searchButtons.push({
            name: 'navigation',
            icon: 'far fa-compass fa-2x',
            hint: 'Navigation Search',
            selected: false,
            visible: true
        });

        this.searchButtons = this.searchButtons.map((r) => {
            return {
                icon: r.icon,
                name: r.name,
                hint: r.hint,
                class: 'button-container',
                selected: r.selected,
                visible: true
            };
        });

        this.selectedButton = this.searchButtons[0];
    }

    private populateCommunities(): void {
        this.loadingVisible = true;
        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communities = result;
                this.populateSearchButtons();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private prepareBookmarkItems(): void {
        if (this.sideMenuItems && this.sideMenuItems.length > 0) {
            const menuItems: PopMenuInfo[] = [];
            let disabledMenuItems: PopMenuInfo[] = [];
            let bookmarkItems: PopMenuInfo[] = [];

            this.sideMenuItems.forEach((sideMenu) => {
                let parentObj: PopMenuInfo = new PopMenuInfo();
                parentObj = Object.assign(parentObj, sideMenu);
                if (this.savedBookmarkItems && this.savedBookmarkItems.length > 0) {
                    const savedBookmark = this.savedBookmarkItems.find((d) => d.name === parentObj.name);
                    if (savedBookmark) {
                        parentObj.bookmarked = true;
                        parentObj.disable = false;
                        parentObj.visibleIndex = savedBookmark.visibleIndex;
                    }

                    if (parentObj.items.length > 0) {
                        parentObj.items = this.setChildItemsBookmark(parentObj.items);
                    }

                    if (parentObj.bookmarked) {
                        let bookmarkNode: PopMenuInfo = new PopMenuInfo();
                        bookmarkNode = Object.assign(bookmarkNode, parentObj);
                        bookmarkNode.items = [];
                        bookmarkItems.push(bookmarkNode);
                    }
                    if (parentObj.items.length > 0) {
                        const childItems = this.getChildBookmarkItems(parentObj.items);
                        childItems.forEach((child) => {
                            if (
                                !bookmarkItems.find((d) => d.id === child.parentId) &&
                                !childItems.find((d) => d.id === child.parentId)
                            ) {
                                child.parentId = null;
                            }
                        });
                        bookmarkItems = bookmarkItems.concat(childItems);
                    }
                } else {
                    parentObj.disable = false;
                    parentObj.bookmarked = false;
                    if (parentObj.items.length > 0) {
                        parentObj.items = this.markItemsAsDisable(parentObj.items, false);
                        parentObj.items = this.markItemsAsBookmark(parentObj.items, false);
                    }
                }
                menuItems.push(parentObj);
            });

            disabledMenuItems = this.getDisabledBookmarkItems();

            this.bookmarkMenuItems = menuItems.concat(disabledMenuItems); // bookmark manager viewer
            this.bookmarkItems = bookmarkItems
                .concat(disabledMenuItems)
                .sort((a, b) => (a.visibleIndex > b.visibleIndex ? 1 : -1)); // bookmark tree

            if (this.bookmarkTree && this.bookmarkTree.instance) {
                this.bookmarkTree.instance.option('items', this.bookmarkItems);
            }
        }
    }

    private getChildBookmarkItems(childItems: PopMenuInfo[]): PopMenuInfo[] {
        let bookmarkChilds: PopMenuInfo[] = [];
        childItems.forEach((item) => {
            if (item.bookmarked) {
                let bookmarkNode: PopMenuInfo = new PopMenuInfo();
                bookmarkNode = Object.assign(bookmarkNode, item);
                bookmarkNode.items = [];
                bookmarkChilds.push(bookmarkNode);
            }
            if (item.items.length > 0) {
                bookmarkChilds = bookmarkChilds.concat(this.getChildBookmarkItems(item.items));
            }
        });

        return bookmarkChilds;
    }

    private getDisabledBookmarkItems(): PopMenuInfo[] {
        const disabledItems: PopMenuInfo[] = [];

        if (this.savedBookmarkItems && this.savedBookmarkItems.length > 0) {
            this.savedBookmarkItems.forEach((bookmarkItem) => {
                const searchItem = this.findItemInNavigation(bookmarkItem, this.sideMenuItems);
                if (!searchItem) {
                    const disabledNode = this.findInMasterNavigation(bookmarkItem, this.masterNavItems);
                    if (!!disabledNode) {
                        const newDisabledItem: PopMenuInfo = new PopMenuInfo();
                        newDisabledItem.name = disabledNode.name;
                        newDisabledItem.text = disabledNode.text;
                        newDisabledItem.path = disabledNode.path;
                        newDisabledItem.icon = disabledNode.icon;
                        newDisabledItem.id = disabledNode.id;
                        newDisabledItem.parentId = disabledNode.parentId;
                        newDisabledItem.items = [];
                        newDisabledItem.disable = true;
                        newDisabledItem.bookmarked = true;
                        newDisabledItem.visibleIndex = bookmarkItem.visibleIndex;

                        disabledItems.push(newDisabledItem);
                    }
                }
            });
        }

        disabledItems.forEach((item) => {
            if (!disabledItems.find((d) => d.id === item.parentId)) {
                item.parentId = null;
            }
        });

        return disabledItems;
    }

    private findInMasterNavigation(bookmarkItem, masterNavItems: SideNavTreeInfo[]): SideNavTreeInfo {
        return this.findItemInList(bookmarkItem, masterNavItems);
    }

    private findItemInNavigation(bookmarkedItem: BookmarkInfo, sideMenuItems: PopMenuInfo[]): PopMenuInfo {
        return this.findItemInList(bookmarkedItem, sideMenuItems);
    }

    private findItemInList(itemToFind: BookmarkInfo, listItems: any[]): any {
        let searchedItem: any;

        for (const item of listItems) {
            if (item.name === itemToFind.name) {
                searchedItem = item;
            } else if (item.items.length > 0) {
                searchedItem = this.findItemInList(itemToFind, item.items);
            }
            if (searchedItem) {
                break;
            }
        }

        return searchedItem;
    }

    private setChildItemsBookmark(childItems): PopMenuInfo[] {
        childItems.forEach((childItem) => {
            const savedBookmark = this.savedBookmarkItems.find((d) => d.name === childItem.name);
            if (savedBookmark) {
                childItem.bookmarked = true;
                childItem.disable = false;
                childItem.visibleIndex = savedBookmark.visibleIndex;
            } else {
                childItem.bookmarked = false;
                childItem.visibleIndex = null;
            }

            if (childItem.items.length > 0) {
                childItem.items = this.setChildItemsBookmark(childItem.items);
            }
        });

        return childItems;
    }

    private markItemsAsDisable(items, mark): PopMenuInfo[] {
        items.forEach((item) => {
            item.disable = mark;
            if (item.items.length > 0) {
                item.items = this.markItemsAsDisable(item.items, mark);
            }
        });

        return items;
    }

    private markItemsAsBookmark(items, mark): PopMenuInfo[] {
        items.forEach((item) => {
            item.bookmarked = mark;
            if (item.items.length > 0) {
                item.items = this.markItemsAsBookmark(item.items, mark);
            }
        });

        return items;
    }

    private updateSelectedContext(): void {
        const selectedContext = this.contextManagerService.getSelectedContext();

        if (Utils.notNullAndDefined(selectedContext)) {
            this.selectedContextCommunityId = selectedContext.communityId;
        } else {
            this.selectedContextCommunityId = null;
        }
        this.populateSearchButtons();
    }
}
